import { useMemo, useState } from "react";
import { Shuffle, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { MOCK_COURSES } from "@/data/courses";
import {
  MOCK_EXERCISES,
  getExerciseById,
  MUSCLE_GROUPS,
  getMuscleGroupOfExercise,
} from "@/data/exercises";
import { useTrainingSchedule } from "@/hooks/useTrainingSchedule";
import {
  courseDayAsMemberDay,
  type MemberPlanDay,
  type ExerciseConfiguration,
} from "@/lib/memberTrainingDay";

type Mode = "same-plan" | "other-plan" | "free";
const COURSE_LABELS: Record<string, string> = {
  personal: "私教",
  "three-way": "三分化",
  "four-way": "四分化",
  "ksw-collection": "凯圣王",
  home: "居家",
  "fanjue-plan": "焚诀",
};
export default function TodayTrainingSwitcher() {
  const { plan, override, disabled, replaceToday } = useTrainingSchedule();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("same-plan");
  const [query, setQuery] = useState("");
  const [part, setPart] = useState("all");
  const [selection, setSelection] = useState<MemberPlanDay | null>(null);
  const [ids, setIds] = useState<string[]>([]);
  const [configs, setConfigs] = useState<Record<string, ExerciseConfiguration>>(
    {},
  );
  const exercises = useMemo(
    () =>
      MOCK_EXERCISES.filter(
        (ex) =>
          (!query || ex.name.includes(query)) &&
          (part === "all" || getMuscleGroupOfExercise(ex) === part),
      ).slice(0, 80),
    [query, part],
  );
  if (!plan) return null;
  const switchMode = (next: Mode) => {
    setMode(next);
    setSelection(null);
    setQuery("");
  };
  const toggle = (id: string) => {
    if (ids.includes(id)) {
      setIds((current) => current.filter((item) => item !== id));
      return;
    }
    if (ids.length >= 20) {
      toast.error("一次最多选择20个动作");
      return;
    }
    const ex = getExerciseById(id)!;
    setIds((current) => [...current, id]);
    setConfigs((current) => ({
      ...current,
      [id]: {
        sets: Math.max(1, Math.min(ex.sets, 10)),
        reps: String(parseInt(ex.reps) || 10),
        restSeconds: ex.restSeconds || 60,
      },
    }));
  };
  const confirm = () => {
    try {
      let day = selection;
      if (mode === "free") {
        if (!ids.length) {
          toast.error("请至少选择一个动作");
          return;
        }
        for (const id of ids) {
          const config = configs[id];
          if (
            !Number.isInteger(config.sets) ||
            config.sets < 1 ||
            config.sets > 10 ||
            !/^\d{1,3}$/.test(config.reps) ||
            +config.reps < 1 ||
            +config.reps > 100 ||
            !Number.isInteger(config.restSeconds) ||
            config.restSeconds < 0 ||
            config.restSeconds > 600
          ) {
            toast.error("组数1–10，次数1–100，组间歇0–600秒");
            return;
          }
        }
        day = {
          dayOfWeek: new Date().getDay() || 7,
          type: "strength",
          label: "今日自由搭配",
          subtitle: `${ids.length}个自选动作`,
          durationMin: Math.max(
            5,
            Math.round(
              ids.reduce(
                (sum, id) =>
                  sum + configs[id].sets * (configs[id].restSeconds + 45),
                0,
              ) / 60,
            ),
          ),
          courseId: plan.weekDays.find((d) => d.courseId)?.courseId,
          courseSystem: plan.courseSystem,
          exerciseIds: [...ids],
          warmupIds: [],
          stretchIds: [],
          exerciseSettings: Object.fromEntries(
            ids.map((id) => [id, configs[id]]),
          ),
        };
      }
      if (!day) {
        toast.error("请选择一个训练日");
        return;
      }
      replaceToday(day, mode);
      setOpen(false);
      toast.success("今日训练已替换，明天恢复原计划");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "替换失败");
    }
  };
  const courses = MOCK_COURSES.filter(
    (c) =>
      (!query ||
        `${COURSE_LABELS[c.system] || ""}${c.name}${c.days.map((d) => d.name).join("")}`.includes(
          query,
        )) &&
      c.system !== "fanjue",
  );
  return (
    <div className="mb-3" data-today-training-switcher>
      <div className="flex justify-end">
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            switchMode("same-plan");
            setOpen(true);
          }}
          className="text-xs text-primary font-semibold flex items-center gap-1 py-1 disabled:opacity-40"
        >
          <Shuffle className="w-3.5 h-3.5" />
          换一换
        </button>
      </div>
      {override && (
        <div className="rounded-xl bg-primary/8 p-2.5 mt-1 text-xs">
          <p className="font-bold text-primary">已改为：{override.day.label}</p>
          <p className="text-muted-foreground mt-1">
            {override.originalDay.label} · 已替换 · 明天恢复原计划
          </p>
          {override.kind === "free" && (
            <p className="text-muted-foreground mt-1">
              计入训练记录与周报，正式计划周期保持原排期
            </p>
          )}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[400px] max-h-[85dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>今日训练换一换</DialogTitle>
            <DialogDescription>
              只改今天，明天自动恢复原计划排期。
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-1">
            {(
              [
                ["same-plan", "本计划"],
                ["other-plan", "其他计划"],
                ["free", "自由搭配"],
              ] as const
            ).map(([key, text]) => (
              <button
                type="button"
                key={key}
                onClick={() => switchMode(key)}
                aria-pressed={mode === key}
                className={`rounded-lg py-2 text-sm font-semibold ${mode === key ? "bg-primary text-white" : "bg-muted"}`}
              >
                {text}
              </button>
            ))}
          </div>
          {mode === "same-plan" && (
            <div className="space-y-2">
              {plan.weekDays
                .filter(
                  (d) =>
                    d.type === "strength" ||
                    d.type === "cardio" ||
                    d.type === "hiit",
                )
                .map((day) => (
                  <button
                    type="button"
                    key={day.dayOfWeek}
                    onClick={() => setSelection(day)}
                    aria-pressed={selection === day}
                    className={`w-full rounded-xl border p-3 text-left ${selection === day ? "border-primary bg-primary/10" : "border-border"}`}
                  >
                    <p className="text-sm font-bold">{day.label}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {day.subtitle} · {day.durationMin}分钟
                    </p>
                  </button>
                ))}
            </div>
          )}
          {mode !== "same-plan" && (
            <input
              aria-label={mode === "free" ? "搜索动作" : "搜索其他计划"}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={
                mode === "free" ? "搜索动作名称" : "搜索课程或训练日"
              }
              className="w-full rounded-xl border bg-background px-3 py-2 text-sm"
            />
          )}
          {mode === "other-plan" && (
            <div className="max-h-[40dvh] overflow-y-auto space-y-3">
              {courses.map((course) => (
                <div key={course.id}>
                  <p className="text-xs font-bold text-muted-foreground mb-1">
                    {COURSE_LABELS[course.system]} · {course.name}
                  </p>
                  {course.days.map((day) => (
                    <button
                      type="button"
                      key={day.id}
                      onClick={() =>
                        setSelection(
                          courseDayAsMemberDay(
                            course,
                            day,
                            new Date().getDay() || 7,
                          ),
                        )
                      }
                      aria-pressed={selection?.courseDayId === day.id}
                      className={`w-full rounded-lg p-2 text-left text-sm mb-1 ${selection?.courseDayId === day.id ? "bg-primary/15 text-primary" : "bg-muted/40"}`}
                    >
                      {day.name} · {day.durationMin}分钟
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
          {mode === "free" && (
            <>
              <select
                aria-label="动作部位"
                value={part}
                onChange={(event) => setPart(event.target.value)}
                className="rounded-xl border bg-background p-2 text-sm"
              >
                <option value="all">所有部位</option>
                {MUSCLE_GROUPS.map((group) => (
                  <option key={group.key} value={group.key}>
                    {group.label}
                  </option>
                ))}
              </select>
              <div className="max-h-[25dvh] overflow-y-auto space-y-1">
                {exercises.map((ex) => (
                  <button
                    type="button"
                    key={ex.id}
                    aria-pressed={ids.includes(ex.id)}
                    onClick={() => toggle(ex.id)}
                    className={`w-full flex items-center justify-between rounded-lg p-2 text-left text-sm ${ids.includes(ex.id) ? "bg-primary/15 text-primary" : "bg-muted/40"}`}
                  >
                    {ex.name}
                    {ids.includes(ex.id) && (
                      <Check className="w-4 h-4 shrink-0" />
                    )}
                  </button>
                ))}
              </div>
              <p className="text-sm font-bold">已选 {ids.length} 个动作</p>
              {ids.map((id) => (
                <div key={id} className="rounded-xl bg-muted/40 p-3">
                  <p className="text-xs font-semibold mb-2">
                    {getExerciseById(id)?.name}
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {(["sets", "reps", "restSeconds"] as const).map(
                      (field, index) => (
                        <label
                          key={field}
                          className="text-[11px] text-muted-foreground"
                        >
                          {["组数", "次数", "组间歇(秒)"][index]}
                          <input
                            type="number"
                            aria-label={`${getExerciseById(id)?.name}${["组数", "次数", "组间歇"][index]}`}
                            min={field === "restSeconds" ? 0 : 1}
                            max={
                              field === "sets"
                                ? 10
                                : field === "reps"
                                  ? 100
                                  : 600
                            }
                            value={configs[id][field]}
                            onChange={(event) =>
                              setConfigs((current) => ({
                                ...current,
                                [id]: {
                                  ...current[id],
                                  [field]:
                                    field === "reps"
                                      ? event.target.value
                                      : Number(event.target.value),
                                },
                              }))
                            }
                            className="w-full rounded-lg border bg-background px-2 py-1.5 mt-1 text-foreground"
                          />
                        </label>
                      ),
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
          <button
            type="button"
            onClick={confirm}
            disabled={disabled || (mode === "free" ? !ids.length : !selection)}
            className="btn-primary-gradient rounded-xl py-3 font-bold text-white disabled:opacity-40"
          >
            换成这次训练
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
